import { useState } from 'react';
import type { AuthoringChapter, AuthoringQuestion, QuestionPayload, QuestionSource } from '../../../school_panel/api/types';
import { Button, FormField } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
import { gradeLabel } from '../../../school_panel/lib/gradeLabel';

export type FieldErrors = Record<string, string>;

export interface FormProps {
  initial?: import('../../../school_panel/api/types').AuthoringQuestion;
  saving: boolean;
  errors: FieldErrors;
  onSave: (payload: QuestionPayload) => void;
  onCancel: () => void;
  /** The subject's chapters (every grade) for the chapter picker. */
  chapters?: AuthoringChapter[];
}

/** Chapter and textbook reference of the question being edited; `payload` goes into the saved body. */
export function useQuestionSource(initial?: AuthoringQuestion) {
  const [chapterId, setChapterId] = useState<number | null>(initial?.chapter?.id ?? null);
  const [reference, setReference] = useState(initial?.reference ?? '');
  // Sent only when set or changed, so an untouched question keeps the same body.
  const startChapter = initial?.chapter?.id ?? null;
  const payload: QuestionSource = {
    ...(chapterId !== null || startChapter !== null ? { chapter_id: chapterId } : {}),
    ...(reference.trim() || initial?.reference ? { reference: reference.trim() } : {}),
  };
  return { chapterId, setChapterId, reference, setReference, payload, initialChapter: initial?.chapter ?? null };
}

/** Chapter picker (grouped by grade) and reference, under each form's explanation. */
export function SourceFields({ source, chapters, errors, idPrefix }: {
  source: ReturnType<typeof useQuestionSource>; chapters?: AuthoringChapter[]; errors: FieldErrors; idPrefix: string;
}) {
  const list = [...(chapters ?? [])];
  // The question's chapter shows even before the list loads.
  if (source.initialChapter && !list.some((c) => c.id === source.initialChapter?.id)) {
    list.push({ id: source.initialChapter.id, name: source.initialChapter.name, grades: [] });
  }
  const groups = new Map<string, AuthoringChapter[]>();
  for (const c of list) {
    const g = c.grades[0] ? gradeLabel(c.grades[0]) : 'Other';
    groups.set(g, [...(groups.get(g) ?? []), c]);
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label="Chapter (optional)" htmlFor={`${idPrefix}-chapter`} error={errors.chapter_id}>
        <select id={`${idPrefix}-chapter`} className={fieldClass} value={source.chapterId ?? ''}
          onChange={(ev) => source.setChapterId(ev.target.value ? Number(ev.target.value) : null)}>
          <option value="">No chapter</option>
          {Array.from(groups, ([g, cs]) => (
            <optgroup key={g} label={g}>
              {cs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </optgroup>
          ))}
        </select>
      </FormField>
      <FormField label="Reference (optional)" htmlFor={`${idPrefix}-ref`} error={errors.reference}>
        <input id={`${idPrefix}-ref`} className={fieldClass} value={source.reference} placeholder="Book, page"
          onChange={(ev) => source.setReference(ev.target.value)} />
      </FormField>
    </div>
  );
}

export function FieldError({ error }: { error?: string }) {
  return error ? <p role="alert" className="mt-1 text-xs text-danger-ink">{error}</p> : null;
}

export function FormFooter({ saving, onCancel }: { saving: boolean; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-2 border-t border-line pt-3">
      <Button variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
      <Button type="submit" loading={saving}>Save question</Button>
    </div>
  );
}

/** Accepted-answers input: Enter adds a chip, a pasted multi-line value adds one
 * chip per line, blur keeps what was typed. Commas are kept ("Addis Ababa, Ethiopia"). */
export function ChipsInput({ label, values, onChange, max = 10 }: {
  label: string; values: string[]; onChange: (v: string[]) => void; max?: number;
}) {
  const [draft, setDraft] = useState('');
  // Commits any number of values in one onChange so a pasted list keeps every line.
  const commitAll = (parts: string[]) => {
    const next = [...values];
    for (const raw of parts) {
      const v = raw.trim();
      if (v && !next.includes(v) && next.length < max) next.push(v);
    }
    setDraft('');
    if (next.length !== values.length) onChange(next);
  };
  const commit = (raw: string) => commitAll([raw]);
  const full = values.length >= max;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line px-2 py-1.5">
      {values.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-mist px-2 py-0.5 text-xs text-ink">
          {v}
          <button type="button" aria-label={`Remove answer ${v}`} className="leading-none" onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
        </span>
      ))}
      <input
        aria-label={label}
        value={draft}
        placeholder={values.length ? 'Another answer, press Enter' : 'Type an answer, press Enter'}
        className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none"
        disabled={full}
        onChange={(e) => {
          const v = e.target.value;
          if (/[\r\n]/.test(v)) commitAll(v.split(/\r?\n|\r/)); else setDraft(v);
        }}
        onPaste={(e) => {
          // A text input drops line breaks from pasted text, so split here.
          const text = e.clipboardData?.getData('text') ?? '';
          if (/[\r\n]/.test(text)) {
            e.preventDefault();
            commitAll([draft, ...text.split(/\r?\n|\r/)]);
          }
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(draft); } }}
        onBlur={() => commit(draft)}
      />
      {full && <span className="text-xs text-muted">At most {max} answers per blank</span>}
    </div>
  );
}
